import { useState, useEffect, useRef } from 'react'
import {
  ChevronDown,
  Play,
  Square,
  Cpu,
  RefreshCw,
  Check,
  AlertCircle,
} from 'lucide-react'
import { useTradingStore } from '../hooks/useTradingStore'
import { useAuthStore } from '../hooks/useAuthStore'

interface StrategySelectorProps {
  compact?: boolean
}

export default function StrategySelector({ compact = false }: StrategySelectorProps) {
  const { role } = useAuthStore()
  const isAdmin = role === 'admin'
  const {
    availableStrategies,
    activeStrategy,
    selectedStrategyId,
    selectedPreset,
    runMode,
    fetchStrategies,
    selectStrategy,
    startSelectedStrategy,
    stopSelectedStrategy,
    setSelectedStrategyId,
    setSelectedPreset,
    setRunMode,
  } = useTradingStore()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fetch strategies on mount
  useEffect(() => {
    fetchStrategies()
  }, [fetchStrategies])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Get current strategy object
  const currentStrategy = availableStrategies.find(s => s.id === selectedStrategyId)

  // Handle strategy selection from dropdown
  const handleStrategySelect = (strategyId: string) => {
    const strategy = availableStrategies.find(s => s.id === strategyId)
    if (strategy) {
      setSelectedStrategyId(strategyId)
      setSelectedPreset(strategy.default_preset)
    }
    setShowDropdown(false)
  }

  // Handle preset change
  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset)
  }

  // Apply strategy selection (load it)
  const applyStrategy = async () => {
    if (!selectedStrategyId || !selectedPreset) return

    setLoading(true)
    setError(null)

    try {
      await selectStrategy(selectedStrategyId, selectedPreset)
      setSuccessMessage('Strategy loaded')
      setTimeout(() => setSuccessMessage(null), 2000)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load strategy')
    } finally {
      setLoading(false)
    }
  }

  // Start strategy
  const handleStart = async () => {
    setLoading(true)
    setError(null)
    try {
      await startSelectedStrategy()
      setSuccessMessage('Strategy started')
      setTimeout(() => setSuccessMessage(null), 2000)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start strategy')
    } finally {
      setLoading(false)
    }
  }

  // Stop strategy
  const handleStop = async () => {
    setLoading(true)
    setError(null)
    try {
      await stopSelectedStrategy()
      setSuccessMessage('Strategy stopped')
      setTimeout(() => setSuccessMessage(null), 2000)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to stop strategy')
    } finally {
      setLoading(false)
    }
  }

  // Refresh strategies
  const handleRefresh = async () => {
    setLoading(true)
    try {
      await fetchStrategies()
    } finally {
      setLoading(false)
    }
  }

  // Check if strategy needs to be loaded (selection differs from active)
  const needsLoading = selectedStrategyId && selectedPreset && (
    !activeStrategy ||
    activeStrategy.strategy_id !== selectedStrategyId ||
    activeStrategy.preset_name !== selectedPreset
  )

  // Compact version for header bar
  if (compact) {
    // Defense-in-depth: viewer must not see strategy controls even if mounted elsewhere
    if (!isAdmin) return null

    return (
      <div className="flex items-center gap-3">
        {/* Strategy Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#1a1a1a] border border-[#333] rounded-lg text-sm text-white hover:border-[#444] transition-colors min-w-[180px]"
          >
            <Cpu size={14} className="text-blue-400" />
            <span className="flex-1 text-left truncate">
              {currentStrategy?.name.replace('Algorithm ', 'Algo ') || 'Select Strategy'}
            </span>
            <ChevronDown size={14} className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showDropdown && (
            <div className="absolute top-full left-0 mt-1 w-72 bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl z-50 max-h-80 overflow-auto">
              {availableStrategies.length === 0 ? (
                <div className="px-4 py-3 text-sm text-[#666]">No strategies found</div>
              ) : (
                availableStrategies.map(strategy => (
                  <button
                    key={strategy.id}
                    onClick={() => handleStrategySelect(strategy.id)}
                    className={`w-full px-4 py-2.5 text-left text-sm hover:bg-[#252525] transition-colors border-b border-[#2a2a2a] last:border-b-0 ${
                      selectedStrategyId === strategy.id ? 'bg-[#252525]' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${selectedStrategyId === strategy.id ? 'text-blue-400' : 'text-white'}`}>
                        {strategy.name}
                      </span>
                      {activeStrategy?.strategy_id === strategy.id && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          activeStrategy.is_running
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {activeStrategy.is_running ? 'Running' : 'Loaded'}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#666] mt-0.5 truncate">{strategy.description}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Preset Selector */}
        {currentStrategy && (
          <select
            value={selectedPreset || ''}
            onChange={(e) => handlePresetChange(e.target.value)}
            className="px-3 py-1.5 bg-[#1a1a1a] border border-[#333] rounded-lg text-sm text-white min-w-[140px]"
          >
            {currentStrategy.presets.map(preset => (
              <option key={preset} value={preset}>{preset}</option>
            ))}
          </select>
        )}

        {/* Mode Indicator */}
        <span className={`text-xs px-2 py-1 rounded ${
          runMode === 'live'
            ? 'bg-green-500/20 text-green-400'
            : runMode === 'backtest'
            ? 'bg-blue-500/20 text-blue-400'
            : 'bg-gray-500/20 text-gray-400'
        }`}>
          {runMode === 'live' ? 'Live' : runMode === 'backtest' ? 'Backtest' : 'Idle'}
        </span>

        {/* Load Button */}
        {needsLoading && (
          <button
            onClick={applyStrategy}
            disabled={loading}
            className="btn btn-primary py-1.5 text-sm"
          >
            {loading ? 'Loading...' : 'Load'}
          </button>
        )}

        {/* Start/Stop Buttons */}
        {activeStrategy && !needsLoading && (
          activeStrategy.is_running ? (
            <button
              onClick={handleStop}
              disabled={loading}
              className="btn btn-outline py-1.5 text-sm flex items-center gap-1"
            >
              <Square size={14} />
              Stop
            </button>
          ) : (
            <button
              onClick={handleStart}
              disabled={loading}
              className="btn btn-success py-1.5 text-sm flex items-center gap-1"
            >
              <Play size={14} />
              Start
            </button>
          )
        )}
      </div>
    )
  }

  // Full version for Settings page
  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center gap-2">
          <Cpu size={16} className="text-blue-400" />
          <h2 className="card-title">Strategy Selection</h2>
        </div>
        <button
          onClick={handleRefresh}
          className="btn btn-outline text-xs py-1 px-2"
          disabled={loading}
          title="Refresh strategies"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
          <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-red-400">{error}</span>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-2">
          <Check size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-green-400">{successMessage}</span>
        </div>
      )}

      <div className="space-y-4">
        {/* Strategy Selection */}
        <div>
          <label className="input-label">Algorithm</label>
          {availableStrategies.length === 0 ? (
            <div className="p-4 bg-[#1a1a1a] rounded-lg text-center text-[#666] text-sm">
              No strategies found. Make sure algorithms are in the algos/ directory.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {availableStrategies.map(strategy => (
                <button
                  key={strategy.id}
                  onClick={() => handleStrategySelect(strategy.id)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    selectedStrategyId === strategy.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-[#333] bg-[#1a1a1a] hover:border-[#444]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-white">{strategy.name}</div>
                      <div className="text-xs text-[#666] mt-1">{strategy.description}</div>
                      <div className="text-xs text-[#555] mt-1">
                        {strategy.presets.length} presets available
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {activeStrategy?.strategy_id === strategy.id && (
                        <span className={`text-xs px-2 py-1 rounded ${
                          activeStrategy.is_running
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {activeStrategy.is_running ? 'Running' : 'Loaded'}
                        </span>
                      )}
                      {selectedStrategyId === strategy.id && (
                        <Check size={16} className="text-blue-400" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Preset Selection */}
        {currentStrategy && (
          <div>
            <label className="input-label">Preset Configuration</label>
            <select
              value={selectedPreset || ''}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="input w-full"
            >
              {currentStrategy.presets.map(preset => (
                <option key={preset} value={preset}>{preset}</option>
              ))}
            </select>
            <p className="text-xs text-[#666] mt-1">
              Each preset contains optimized parameters for different market conditions.
              Default: <span className="text-blue-400">{currentStrategy.default_preset}</span>
            </p>
          </div>
        )}

        {/* Instrument Info */}
        {currentStrategy && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="input-label">Default Backtest Instrument</label>
              <input
                type="text"
                className="input"
                value={currentStrategy.default_instrument || 'Not specified'}
                disabled
              />
            </div>
            <div>
              <label className="input-label">Default Live Instrument</label>
              <input
                type="text"
                className="input"
                value={currentStrategy.default_instrument_live || 'Not specified'}
                disabled
              />
            </div>
          </div>
        )}

        {/* Run Mode Toggle */}
        <div>
          <label className="input-label">Run Mode</label>
          <div className="flex gap-2">
            <button
              onClick={() => setRunMode('live')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                runMode === 'live'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : 'bg-[#1a1a1a] text-[#888] border border-[#333] hover:border-[#444]'
              }`}
            >
              Live Trading
            </button>
            <button
              onClick={() => setRunMode('backtest')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                runMode === 'backtest'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                  : 'bg-[#1a1a1a] text-[#888] border border-[#333] hover:border-[#444]'
              }`}
            >
              Backtest
            </button>
          </div>
          <p className="text-xs text-[#666] mt-1">
            {runMode === 'live'
              ? 'Execute trades in real-time with your broker.'
              : 'Test strategy against historical data. Configure in Backtest tab.'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-[#2a2a2a]">
          <button
            onClick={applyStrategy}
            disabled={loading || !selectedStrategyId || !needsLoading}
            className={`btn flex-1 ${needsLoading ? 'btn-primary' : 'btn-outline'}`}
          >
            {loading ? 'Loading...' : needsLoading ? 'Load Strategy' : 'Strategy Loaded'}
          </button>

          {activeStrategy && (
            activeStrategy.is_running ? (
              <button
                onClick={handleStop}
                disabled={loading}
                className="btn btn-danger flex items-center gap-2"
              >
                <Square size={16} />
                Stop
              </button>
            ) : (
              <button
                onClick={handleStart}
                disabled={loading || !!needsLoading}
                className="btn btn-success flex items-center gap-2"
              >
                <Play size={16} />
                Start
              </button>
            )
          )}
        </div>
      </div>
    </div>
  )
}
